/**************************************************************************************************/
/** @import Classes/Records                                                                      **/
/**************************************************************************************************/
import FieldsOptions from '@/Classes/Records/FieldsOptions'
import { ItemMissingFields } from '@/Classes/Records/ItemMissingFields'
import { RecordHelper } from '@/Classes/Records/RecordHelper'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { ActionFields } from '@/Constants/ActionFields'
import { AppValues } from '@/Constants/AppValues'

/**************************************************************************************************/
/** @import Helpers/Components                                                                   **/
/**************************************************************************************************/
import { BuilderManager } from '@/Helpers/Components/DataTable/BuilderManager'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { TypesHelper } from '@/Helpers/Utils/TypesHelper'

/**************************************************************************************************/
/** @export Function @name parseCompaniesResponse                                                **/
/**************************************************************************************************/
export function parseCompaniesResponse(data: Array<any>)
{
	// Clase Constructora.
	const bm = new BuilderManager(data, ActionFields.SingleOptionEdit)

	// Eliminar los campos que no queremos mostrar en la tabla.
	bm.removeFields('_idAdminCompany', '_idCompany', 'actions', 'region', 'storages')

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('rut', 'Rut').isSortable().setAlign(null, 'center')
		.add('code', 'Código').isSortable().setAlign(null, 'center')
		.add('name', 'Razón Social').isSortable()
		.add('address', 'Dirección').isSortable().setSearchKey('address.name')
		.add('commune', 'Comuna').isSortable().setAlign(null, 'center').setSearchKey('address.commune')
		.add('type', 'Tipo').isSortable().setAlign(null, 'center')
		.add('isClient', '¿Es Cliente?').isSortable().setAlign(null, 'center')
		.add('isValid', '¿Esta Activa?').isSortable().setAlign(null, 'center')
	)

	// Aplicar un nuevo Orden a todas las columnas visibles.
	bm.rearrangeFields('rut', 'code', 'name', 'address', 'commune', 'type', 'isClient', 'isValid')

	// Aplicar un valor a los campos e items si no estan presentes.
	bm.parseItemsMissingFields(new ItemMissingFields()
		.add('address')
		.add('commune')
	)

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		isClient: (v: boolean) => v ? 'Sí' : 'No',
		isValid:  (v: boolean) => v ? 'Sí' : 'No',
		rut:      (v: string)  => v.toUpperCase(),
		type:     (v: string)  =>
		{
			switch (v)
			{
				case 'C': return 'Cliente'
				case 'P': return 'Proveedor'
				case 'S': return 'Sucursal Interna'
			}
		}
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}

/**************************************************************************************************/
/** @export Function @name parseStackedCompaniesResponse                                         **/
/**************************************************************************************************/
export function parseStackedCompaniesResponse(data: Array<any>)
{
	// Clase Constructora.
	const bm = new BuilderManager(data)

	// Eliminar los campos que no queremos mostrar en la tabla.
	bm.removeFields('_idAdminCompany', '_idCompany', 'address', 'code', 'commune', 'isClient', 'isValid', 'region', 'type')

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('rut', 'Rut').isSortable().setAlign(null, 'center').showDetailsButton()
		.add('name', 'Razón Social').isSortable()
	)

	// Aplicar un nuevo Orden a todas las columnas visibles.
	bm.rearrangeFields('rut', 'name')

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		rut: (v: string) =>
		{
			return v.toUpperCase()
		},

		isClient: (v: boolean) =>
		{
			return v ? 'Sí' : 'No'
		},

		isValid: (v: boolean) =>
		{
			return v ? 'Sí' : 'No'
		}
	})

	// Definir los campos a mostrar en el Detalle.
	bm.parseItemsDetail({
		[RecordHelper.setFlags('Código', 'code')]: ({ code }) =>
		{
			return code
		},
		[RecordHelper.setFlags('Dirección', 'address', 'address.name')]: ({ address }) =>
		{
			return address
		},
		[RecordHelper.setFlags('Comuna', 'commune', 'address.commune')]: ({ commune }) =>
		{
			return commune
		},
		[RecordHelper.setFlags('Tipo', 'type')]: ({ type }) =>
		{
			switch (type)
			{
				case 'C': return 'Cliente'
				case 'P': return 'Proveedor'
				case 'S': return 'Sucursal Interna'
			}
		},
		[RecordHelper.setFlags('¿Es Cliente?', 'isClient')]: ({ isClient }) =>
		{
			return isClient ? 'Sí' : 'No'
		},
		[RecordHelper.setFlags('¿Esta Activa?', 'isValid')]: ({ isValid }) =>
		{
			return isValid ? 'Sí' : 'No'
		}
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}

/**************************************************************************************************/
/** @export Function @name parseStoragesResponse                                                 **/
/**************************************************************************************************/
export function parseStoragesResponse(data: Array<any>, removeActionField?: boolean)
{
	// Clase Constructora.
	const bm = new BuilderManager(data, ActionFields.SingleOptionEdit)

	// Eliminar los campos que no queremos mostrar en la tabla.
	const fieldsToRemove = ['_idStorage', '_idCompany', '_idTechnical', '_idUserAdmin', 'codeAlmacenSap', 'contacts', 'equipments', 'region', 'commune']
	if (removeActionField) fieldsToRemove.push('actions')
	bm.removeFields(...fieldsToRemove)

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('accessTotem', 'Acceso Totem').isSortable().setAlign(null, 'center')
		.add('code', 'Código').isSortable().setAlign(null, 'center')
		.add('codeLocation', 'Código Planta').isSortable().setAlign(null, 'center')
		.add('name', 'Nombre').isSortable()
		.add('address', 'Dirección').isSortable().setSearchKey('address.name')
		.add('companyName', 'Cliente').isSortable().setSearchKey('company.name')
		.add('technicalName', 'Técnico').isSortable().setAlign(null, 'center').setSearchKey('technical.name')
		.add('userAdminName', 'Adm. Contrato').isSortable().setAlign(null, 'center').setSearchKey('userAdmin.name')
		.add('isValid', '¿Esta Activa?').isSortable().setAlign(null, 'center')
	)

	// Aplicar un nuevo Orden a todas las columnas visibles.
	bm.rearrangeFields('code', 'codeLocation', 'name', 'address', 'companyName', 'technicalName', 'userAdminName', 'accessTotem', 'isValid')

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		accessTotem: (v: string) =>
		{
			return v ? 'Habilitado' : 'Restringido'
		},

		address: (v: string) =>
		{
			if (TypesHelper.isStringInvalidOrEmpty(v)) return AppValues.Strings.DEFAULT_EMPTY_STRING
			return v
		},

		companyName: (v: string) =>
		{
			return v ? v : AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		isValid: (v: boolean) =>
		{
			return v ? 'Sí' : 'No'
		},

		technicalName: (v: string) =>
		{
			return v ? v : AppValues.Strings.DEFAULT_EMPTY_STRING
		},

		userAdminName: (v: string) =>
		{
			return v ? v : AppValues.Strings.DEFAULT_EMPTY_STRING
		}
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}

/**************************************************************************************************/
/** @export Function @name parseStackedStoragesResponse                                          **/
/**************************************************************************************************/
export function parseStackedStoragesResponse(data: Array<any>)
{
	// Clase Constructora.
	const bm = new BuilderManager(data)

	// Eliminar los campos que no queremos mostrar en la tabla.
	bm.removeFields(
		'_idStorage', '_idCompany', '_idTechnical', '_idUserAdmin', 'address', 'codeLocation', 'commune', 'companyName', 'contacts', 'equipments', 'isValid', 'region', 'technicalName', 'userAdminName'
	)

	// Aplicar opciones a los campos.
	bm.parseFieldsOptions(new FieldsOptions()
		.add('code', 'Código').isSortable().setAlign(null, 'center').showDetailsButton()
		.add('name', 'Nombre').isSortable()
	)

	// Aplicar un nuevo Orden a todas las columnas visibles.
	bm.rearrangeFields('code', 'name')

	// Definir los formatos que serán aplicados al valor de la celda.
	bm.parseItemsFormatters({
		isValid: (v: boolean) =>
		{
			return v ? 'Sí' : 'No'
		}
	})

	// Definir los campos a mostrar en el Detalle.
	bm.parseItemsDetail({
		[RecordHelper.setFlags('Código Planta', 'codeLocation')]: ({ codeLocation }) =>
		{
			return codeLocation
		},
		[RecordHelper.setFlags('Dirección', 'address', 'address.name')]: ({ address }) =>
		{
			if (TypesHelper.isStringInvalidOrEmpty(address)) return AppValues.Strings.DEFAULT_EMPTY_STRING
			return address
		},
		[RecordHelper.setFlags('Cliente', 'companyName', 'company.name')]: ({ companyName }) =>
		{
			return companyName || AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		[RecordHelper.setFlags('Técnico', 'technicalName', 'technical.name')]: ({ technicalName }) =>
		{
			return technicalName || AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		[RecordHelper.setFlags('Adm. Contrato', 'userAdminName', 'userAdmin.name')]: ({ userAdminName }) =>
		{
			return userAdminName || AppValues.Strings.DEFAULT_EMPTY_STRING
		},
		['¿Es Cliente?']: ({ isClient }) =>
		{
			return isClient ? 'Sí' : 'No'
		},
		[RecordHelper.setFlags('¿Esta Activa?', 'isValid')]: ({ isValid }) =>
		{
			return isValid ? 'Sí' : 'No'
		},
	})

	// Retornar Objeto con los Campos, Items y Acciones.
	return bm.packWithActions()
}